import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Baby, ArrowLeft, Camera, Ruler, Save } from 'lucide-react';
import { convertLengthToSize, calculateAge, getRecommendation } from '../mock';
import { toast } from '../hooks/use-toast';
import { measurementsAPI, subscriptionAPI } from '../services/api';
import Logo from '../components/Logo';

const MeasureFeet = () => {
  const navigate = useNavigate();
  const { childId } = useParams();
  const { children, addMeasurement } = useAuth();
  const child = children.find(c => c.id === childId);
  
  const [manualData, setManualData] = useState({ length: '', width: '' });
  const [cameraData, setCameraData] = useState({ referenceSize: 'credit_card', length: '', width: '' });
  const [result, setResult] = useState(null);
  const [activeTab, setActiveTab] = useState('manual');
  const [imagePreview, setImagePreview] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [hasSubscription, setHasSubscription] = useState(false);
  const [subscriptionInfo, setSubscriptionInfo] = useState(null);
  const [checkingSubscription, setCheckingSubscription] = useState(true);

  // Check subscription status on page load
  React.useEffect(() => {
    if (!childId) return;
    
    const checkSubscription = async () => {
      setCheckingSubscription(true);
      try {
        const result = await subscriptionAPI.getStatus();
        console.log('Subscription check result:', result);
        
        setHasSubscription(result.has_subscription);
        setSubscriptionInfo(result);
        
        if (!result.has_subscription) {
          // Redirect to subscribe page
          navigate('/subscribe');
        }
      } catch (error) {
        console.error('Subscription check error:', error);
        setHasSubscription(false);
        navigate('/subscribe');
      } finally {
        setCheckingSubscription(false);
      }
    };
    checkSubscription();
  }, [childId, navigate]);

  if (!child) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p>Child not found</p>
      </div>
    );
  }

  // Remove old payment handling - now using subscription

  const handleManualMeasure = (e) => {
    e.preventDefault();
    const lengthCm = parseFloat(manualData.length);
    const widthCm = parseFloat(manualData.width);
    
    // Validate that values are valid numbers
    if (isNaN(lengthCm) || isNaN(widthCm)) {
      toast({
        title: 'Missing measurements',
        description: 'Please enter both foot length and width.',
        variant: 'destructive'
      });
      return;
    }
    
    if (lengthCm < 5 || lengthCm > 20) {
      toast({
        title: 'Invalid measurement',
        description: 'Length should be between 5-20 cm for babies 0-2 years',
        variant: 'destructive'
      });
      return;
    }

    const sizes = convertLengthToSize(lengthCm);
    const ageMonths = calculateAge(child.date_of_birth || child.dateOfBirth);
    const recommendation = getRecommendation(ageMonths, lengthCm);

    const measurementData = {
      childId: child.id,
      date: new Date().toISOString().split('T')[0],
      lengthCm,
      widthCm,
      method: 'manual',
      usSize: sizes.us,
      euSize: sizes.eu,
      ukSize: sizes.uk,
      ageMonths
    };

    setResult({
      ...measurementData,
      recommendation
    });
  };

  const handleCameraMeasure = (e) => {
    e.preventDefault();
    const lengthCm = parseFloat(cameraData.length);
    const widthCm = parseFloat(cameraData.width);
    
    // Validate that values are valid numbers
    if (isNaN(lengthCm) || isNaN(widthCm)) {
      toast({
        title: 'Missing measurements',
        description: 'Please upload an image and wait for AI analysis to complete, or enter measurements manually.',
        variant: 'destructive'
      });
      return;
    }
    
    if (lengthCm < 5 || lengthCm > 20) {
      toast({
        title: 'Invalid measurement',
        description: 'Length should be between 5-20 cm for babies 0-2 years',
        variant: 'destructive'
      });
      return;
    }

    const sizes = convertLengthToSize(lengthCm);
    const ageMonths = calculateAge(child.date_of_birth || child.dateOfBirth);
    const recommendation = getRecommendation(ageMonths, lengthCm);

    const measurementData = {
      childId: child.id,
      date: new Date().toISOString().split('T')[0],
      lengthCm,
      widthCm,
      method: 'camera',
      usSize: sizes.us,
      euSize: sizes.eu,
      ukSize: sizes.uk,
      ageMonths
    };

    setResult({
      ...measurementData,
      recommendation
    });
  };

  const handleImageCapture = async (e) => {
    const file = e.target.files[0];
    if (file) {
      // Show preview
      const reader = new FileReader();
      reader.onload = (e) => setImagePreview(e.target.result);
      reader.readAsDataURL(file);
      
      // Analyze with AI
      setAnalyzing(true);
      try {
        // Convert image to base64 for API
        const base64Reader = new FileReader();
        base64Reader.onload = async (e) => {
          try {
            const base64String = e.target.result.split(',')[1]; // Remove data:image/jpeg;base64, prefix
            
            // Call AI analysis API
            const analysisResult = await measurementsAPI.analyzeImage(base64String, cameraData.referenceSize);
            
            if (analysisResult.success) {
              // Update camera data with AI results
              setCameraData(prev => ({
                ...prev,
                length: analysisResult.length_cm.toString(),
                width: analysisResult.width_cm.toString()
              }));
              
              toast({
                title: 'AI Analysis Complete!',
                description: `Measurements extracted: ${analysisResult.length_cm}cm x ${analysisResult.width_cm}cm (${analysisResult.confidence} confidence)`,
              });
              
              if (analysisResult.notes) {
                toast({
                  title: 'AI Note',
                  description: analysisResult.notes,
                });
              }
            } else {
              throw new Error(analysisResult.error || 'Analysis failed');
            }
          } catch (error) {
            console.error('AI analysis error:', error);
            toast({
              title: 'Analysis Failed',
              description: error.message || 'Unable to analyze the image. Please ensure good lighting and clear visibility of both foot and reference object.',
              variant: 'destructive'
            });
          } finally {
            setAnalyzing(false);
          }
        };
        base64Reader.readAsDataURL(file);
      } catch (error) {
        console.error('Image reading error:', error);
        setAnalyzing(false);
        toast({
          title: 'Error',
          description: 'Failed to read image file',
          variant: 'destructive'
        });
      }
    }
  };

  const handleSave = async () => {
    if (result) {
      // Validate before saving
      if (isNaN(result.lengthCm) || isNaN(result.widthCm) || result.lengthCm <= 0 || result.widthCm <= 0) {
        toast({
          title: 'Invalid measurement',
          description: 'Cannot save - measurement values are invalid. Please re-enter the measurements.',
          variant: 'destructive'
        });
        return;
      }
      
      try {
        const { recommendation, ...measurementData } = result;
        await addMeasurement(measurementData);
        toast({
          title: 'Success!',
          description: 'Measurement saved successfully.',
        });
        navigate(`/child/${childId}`);
      } catch (error) {
        console.error('Save measurement error:', error);
        
        // Extract proper error message
        let errorMessage = 'Failed to save measurement. Please try again.';
        
        if (error.response?.data?.detail) {
          const detail = error.response.data.detail;
          if (Array.isArray(detail)) {
            // FastAPI validation errors array
            errorMessage = detail.map(err => err.msg).join(', ');
          } else if (typeof detail === 'string') {
            errorMessage = detail;
          }
        } else if (error.message) {
          errorMessage = error.message;
        }
        
        toast({
          title: 'Error',
          description: errorMessage,
          variant: 'destructive'
        });
      }
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#FFF5F0] via-[#E5F5FF] to-[#F0FFE5]">
      {/* Header */}
      <nav className="border-b bg-white/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <Logo />
          <Button variant="outline" onClick={() => navigate(`/child/${childId}`)}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
        </div>
      </nav>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-800 mb-2">Measure {child.name}'s Feet</h1>
          <p className="text-gray-600">Choose your preferred measurement method</p>
        </div>

        {/* Subscription Status Notice */}
        {checkingSubscription ? (
          <Card className="p-8 mb-6 bg-white/90 backdrop-blur-sm text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto mb-2"></div>
            <p className="text-gray-600">Checking subscription status...</p>
          </Card>
        ) : hasSubscription && subscriptionInfo ? (
          <Card className="p-4 mb-6 bg-green-50 border-green-200">
            <div className="flex items-center justify-between">
              <p className="text-green-700">✓ Subscription active</p>
              <p className="text-sm text-gray-600">
                {subscriptionInfo.measurements_remaining} measurement{subscriptionInfo.measurements_remaining !== 1 ? 's' : ''} remaining this month
              </p>
            </div>
            {subscriptionInfo.measurements_remaining === 0 && (
              <p className="text-amber-600 text-sm mt-2">
                You've used all your measurements for this month. Your limit will reset at the start of your next billing period.
              </p>
            )}
          </Card>
        ) : null}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Measurement Input */}
          <Card className="p-6 bg-white/90 backdrop-blur-sm relative">
            {(!hasSubscription || (subscriptionInfo && subscriptionInfo.measurements_remaining === 0)) && (
              <div className="absolute inset-0 bg-gray-100/70 backdrop-blur-sm z-10 flex items-center justify-center rounded-lg">
                <div className="text-center">
                  <p className="text-gray-700 font-semibold mb-2">
                    {!hasSubscription ? '🔒 Subscription Required' : '📊 Monthly Limit Reached'}
                  </p>
                  <p className="text-sm text-gray-600">
                    {!hasSubscription 
                      ? 'Subscribe to take measurements'
                      : 'Your 2 measurements for this month have been used'}
                  </p>
                </div>
              </div>
            )}
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="grid w-full grid-cols-2 mb-6">
                <TabsTrigger value="manual">
                  <Ruler className="w-4 h-4 mr-2" />
                  Manual
                </TabsTrigger>
                <TabsTrigger value="camera">
                  <Camera className="w-4 h-4 mr-2" />
                  Camera
                </TabsTrigger>
              </TabsList>

              <TabsContent value="manual">
                <form onSubmit={handleManualMeasure} className="space-y-4">
                  <div className="bg-blue-50 p-4 rounded-lg mb-4">
                    <p className="text-sm text-gray-700">
                      <strong>How to measure:</strong> Place your baby's foot on a ruler or measuring tape. Measure from heel to longest toe.
                    </p>
                  </div>
                  <div>
                    <Label htmlFor="manual-length">Foot Length (cm) *</Label>
                    <Input
                      id="manual-length"
                      type="number"
                      step="0.1"
                      placeholder="e.g., 12.5"
                      value={manualData.length}
                      onChange={(e) => setManualData({ ...manualData, length: e.target.value })}
                      required
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="manual-width">Foot Width (cm) *</Label>
                    <Input
                      id="manual-width"
                      type="number"
                      step="0.1"
                      placeholder="e.g., 5.8"
                      value={manualData.width}
                      onChange={(e) => setManualData({ ...manualData, width: e.target.value })}
                      required
                      className="mt-1"
                    />
                  </div>
                  <Button type="submit" className="w-full bg-gradient-to-r from-[#FFB4A2] via-[#F7DBA7] to-[#A8D5FF] hover:from-pink-600 hover:to-purple-600 text-white">
                    Calculate Size
                  </Button>
                </form>
              </TabsContent>

              <TabsContent value="camera">
                <form onSubmit={handleCameraMeasure} className="space-y-4">
                  <div className="bg-blue-50 p-4 rounded-lg mb-4">
                    <p className="text-sm text-gray-700 mb-3">
                      <strong>AI Vision Measurement - How it works:</strong>
                    </p>
                    <ol className="text-sm text-gray-700 space-y-2 list-decimal list-inside">
                      <li>Place a credit card (8.56cm x 5.4cm) or A4 paper next to the foot</li>
                      <li>Ensure good lighting and clear visibility of both foot and reference</li>
                      <li>Take a photo - AI will analyze and calculate measurements</li>
                      <li>Review results and save</li>
                    </ol>
                  </div>

                  <div>
                    <Label htmlFor="reference-type">Reference Object *</Label>
                    <select
                      id="reference-type"
                      value={cameraData.referenceSize}
                      onChange={(e) => setCameraData({ ...cameraData, referenceSize: e.target.value })}
                      className="w-full mt-1 px-3 py-2 border rounded-md"
                      required
                    >
                      <option value="credit_card">Credit Card (8.56cm x 5.4cm)</option>
                      <option value="a4_paper">A4 Paper (21cm x 29.7cm)</option>
                    </select>
                  </div>

                  <div>
                    <Label htmlFor="camera-capture">Take Photo *</Label>
                    <input
                      id="camera-capture"
                      type="file"
                      accept="image/*"
                      capture="environment"
                      onChange={handleImageCapture}
                      className="w-full mt-1 px-3 py-2 border rounded-md"
                      required
                    />
                    {imagePreview && (
                      <div className="mt-2">
                        <img src={imagePreview} alt="Foot preview" className="max-w-full h-48 object-contain border rounded" />
                      </div>
                    )}
                  </div>

                  <Button 
                    type="submit" 
                    className="w-full bg-gradient-to-r from-[#FFB4A2] via-[#F7DBA7] to-[#A8D5FF] hover:from-pink-600 hover:to-purple-600 text-white"
                    disabled={analyzing}
                  >
                    {analyzing ? 'Analyzing Image...' : 'Analyze with AI'}
                  </Button>
                </form>
              </TabsContent>
            </Tabs>
          </Card>

          {/* Results */}
          <Card className="p-6 bg-white/90 backdrop-blur-sm">
            {result ? (
              <div>
                <h3 className="text-xl font-bold text-gray-800 mb-4">Measurement Results</h3>
                
                <div className="space-y-4">
                  <div>
                    <p className="text-sm text-gray-600 mb-2">Shoe Sizes</p>
                    <div className="grid grid-cols-3 gap-3">
                      <div className="bg-gradient-to-br from-pink-100 to-pink-200 rounded-lg p-4 text-center">
                        <p className="text-xs text-gray-600 mb-1">US Size</p>
                        <p className="text-2xl font-bold text-[#FFB4A2]">{result.usSize}</p>
                      </div>
                      <div className="bg-gradient-to-br from-purple-100 to-purple-200 rounded-lg p-4 text-center">
                        <p className="text-xs text-gray-600 mb-1">EU Size</p>
                        <p className="text-2xl font-bold text-[#A8D5FF]">{result.euSize}</p>
                      </div>
                      <div className="bg-gradient-to-br from-blue-100 to-blue-200 rounded-lg p-4 text-center">
                        <p className="text-xs text-gray-600 mb-1">UK Size</p>
                        <p className="text-2xl font-bold text-[#9ADFB4]">{result.ukSize}</p>
                      </div>
                    </div>
                  </div>

                  <div>
                    <p className="text-sm text-gray-600 mb-2">Measurements</p>
                    <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Length:</span>
                        <span className="font-semibold">{result.lengthCm} cm</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Width:</span>
                        <span className="font-semibold">{result.widthCm} cm</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Method:</span>
                        <span className="font-semibold capitalize">{result.method}</span>
                      </div>
                    </div>
                  </div>

                  <div className={`rounded-lg p-4 ${
                    result.recommendation.status === 'normal' ? 'bg-green-50 border border-green-200' :
                    result.recommendation.status === 'small' ? 'bg-yellow-50 border border-yellow-200' :
                    'bg-orange-50 border border-orange-200'
                  }`}>
                    <p className="font-semibold mb-1 text-gray-800">{result.recommendation.message}</p>
                    <p className="text-sm text-gray-600">{result.recommendation.recommendation}</p>
                  </div>

                  <Button
                    onClick={handleSave}
                    className="w-full bg-gradient-to-r from-[#FFB4A2] via-[#F7DBA7] to-[#A8D5FF] hover:from-pink-600 hover:to-purple-600 text-white"
                  >
                    <Save className="w-4 h-4 mr-2" />
                    Save Measurement
                  </Button>
                </div>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center h-full text-center py-12">
                <div className="w-20 h-20 bg-gradient-to-br from-[#FFB4A2] to-[#F7DBA7] rounded-full flex items-center justify-center mb-4">
                  <Ruler className="w-10 h-10 text-white" />
                </div>
                <h3 className="text-lg font-semibold text-gray-800 mb-2">No Results Yet</h3>
                <p className="text-gray-600 text-sm">Enter measurements to see shoe size recommendations</p>
              </div>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
};

export default MeasureFeet;